import { useState, useMemo } from "react";
import { useExperiences } from "@/hooks/use-experiences";
import { useExperienceReviews } from "@/hooks/use-experience-reviews";
import { CategoryFilter } from "./CategoryFilter";
import { SeasonFilter } from "./SeasonFilter";
import { DateFilter } from "./DateFilter";
import { TopDestinations } from "./TopDestinations";
import { ExperienceCard } from "./ExperienceCard";
import { Skeleton } from "@/components/ui/skeleton";
import { useUiTranslations } from "@/hooks/use-ui-translations";

function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="aspect-[3/2] w-full rounded-lg" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ))}
    </div>
  );
}

export function ExperienceGrid() {
  const { data: experiences, isLoading, error } = useExperiences();
  const { data: reviewStats } = useExperienceReviews();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const { t } = useUiTranslations();

  const categories = useMemo(() => {
    if (!experiences) return [];
    const cats = [...new Set(experiences.map((e) => e.category).filter(Boolean))] as string[];
    return cats.sort();
  }, [experiences]);

  const filtered = useMemo(() => {
    if (!experiences) return [];
    let result = experiences;
    if (selectedCategory) result = result.filter((e) => e.category === selectedCategory);
    if (selectedSeason) result = result.filter((e) => e.season === selectedSeason);
    // Date filter would need slot data — for now we show all on date selection as visual indicator
    return result;
  }, [experiences, selectedCategory, selectedSeason, selectedDate]);

  if (isLoading) return <LoadingSkeleton />;

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center text-destructive">
        {t("grid.loadError")}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TopDestinations experiences={experiences} />

      <div className="flex flex-wrap items-center gap-2">
        <CategoryFilter
          categories={categories}
          selected={selectedCategory}
          onSelect={setSelectedCategory}
        />
        <SeasonFilter
          selected={selectedSeason}
          onSelect={setSelectedSeason}
        />
        <DateFilter
          selected={selectedDate}
          onSelect={setSelectedDate}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-lg text-muted-foreground">{t("noExperiences")}</p>
          {selectedCategory && (
            <button
              onClick={() => setSelectedCategory(null)}
              className="mt-2 text-sm text-primary underline-offset-4 hover:underline"
            >
              {t("clearFilter")}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {filtered.map((exp) => (
            <ExperienceCard key={exp.id} experience={exp} compact reviewScore={reviewStats?.[exp.id] ?? null} />
          ))}
        </div>
      )}
    </div>
  );
}
